import { useCallback, useEffect, useState } from "react";
import "@/App.css";
import axios from "axios";
import AdminApp from "@/AdminApp";
import {
  Target,
  Code2,
  Brush,
  Search,
  Users,
  Bot,
  Terminal,
  Headphones,
  Film,
  Camera,
  ArrowUpRight,
  Sparkles,
  Instagram,
  Mail,
  Star,
  ChevronRight,
} from "lucide-react";
import { Toaster, toast } from "sonner";
import { NAV, HERO, ABOUT, SERVICES, RESULTS, TRUSTED, BOOKING, FOOTER } from "@/constants/testIds";
import { SERVICES_DATA, ASSETS, CLIENT_LOGOS } from "@/data/services";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SCROLL_THRESHOLD_PX = 30;
const HERO_STAR_COUNT = 5;

const ICON_MAP = {
  Target, Code2, Brush, Search, Users, Bot, Terminal, Headphones, Film, Camera,
};

const TOASTER_OPTIONS = {
  style: {
    background: "var(--bg-elevated)",
    color: "var(--text-primary)",
    border: "1px solid var(--border-metal)",
    fontFamily: "Outfit, sans-serif",
  },
};

/* ---------- Custom hooks ---------- */
const useScrollPastThreshold = (thresholdPx) => {
  const [past, setPast] = useState(false);

  const handleScroll = useCallback(() => {
    setPast(window.scrollY > thresholdPx);
  }, [thresholdPx]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  return past;
};

/* ---------- Reusable bits ---------- */
const Overline = ({ children, className = "" }) => (
  <div className={`font-mono-accent text-[11px] tracking-[0.32em] uppercase text-[var(--gold)] ${className}`}>
    <span className="inline-block w-8 h-px bg-[var(--gold)] align-middle mr-3" />
    {children}
  </div>
);

const SectionDivider = () => (
  <div className="max-w-7xl mx-auto px-6 lg:px-12">
    <div className="gold-hairline w-full" />
  </div>
);

const MARQUEE_ITEMS = [
  "Paid Ads", "Web Design", "Creative", "SEO · AI SEO", "Social Media",
  "AI Automation", "Software", "Virtual Assistants", "Cinematography", "Photography",
];

const HeroMarquee = () => (
  <div className="absolute bottom-0 left-0 right-0 overflow-hidden border-y border-[var(--border-metal)] bg-black/40 py-4">
    <div className="flex marquee-track whitespace-nowrap">
      {["a", "b"].map((group) => (
        <div key={`marquee-${group}`} className="flex items-center gap-12 pr-12">
          {MARQUEE_ITEMS.map((item) => (
            <span key={`${group}-${item}`} className="flex items-center gap-12">
              <span className="font-display italic text-2xl text-[var(--text-secondary)]">{item}</span>
              <Sparkles size={14} className="text-[var(--gold)]" strokeWidth={1.5} />
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

/* ---------- Navigation ---------- */
const NAV_LINKS = [
  { testid: NAV.linkAbout, href: "#about", label: "Ethos" },
  { testid: NAV.linkServices, href: "#services", label: "Services" },
  { testid: NAV.linkResults, href: "#results", label: "Proof" },
  { testid: NAV.linkBook, href: "#book", label: "Contact" },
];

const NavLogo = () => (
  <a href="#top" data-testid={NAV.logo} className="flex items-center gap-3 group">
    <img src={ASSETS.logo} alt="The Closer.irl" className="h-12 w-auto" />
    <div className="hidden sm:block leading-tight">
      <div className="font-display text-[15px] font-semibold tracking-wider text-[var(--text-primary)]">
        HARVER G. LUCIANO
      </div>
      <div className="font-mono-accent text-[9px] tracking-[0.3em] text-[var(--gold)] uppercase">
        The Closer.irl
      </div>
    </div>
  </a>
);

const NavLinks = () => (
  <div className="hidden md:flex items-center gap-10 font-mono-accent text-[11px] tracking-[0.25em] uppercase">
    {NAV_LINKS.map((link) => (
      <a
        key={link.href}
        data-testid={link.testid}
        href={link.href}
        className="text-[var(--text-secondary)] hover:text-[var(--gold-bright)] transition"
      >
        {link.label}
      </a>
    ))}
  </div>
);

const NavCta = () => (
  <a
    data-testid={NAV.ctaBook}
    href="#book"
    className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 border border-[var(--gold)] text-[var(--gold-bright)] font-mono-accent text-[11px] tracking-[0.28em] uppercase hover:bg-[var(--gold)] hover:text-black transition-all duration-400 gold-glow-hover"
  >
    Book a call <ArrowUpRight size={14} strokeWidth={1.5} />
  </a>
);

const Navigation = () => {
  const scrolled = useScrollPastThreshold(SCROLL_THRESHOLD_PX);

  return (
    <nav
      data-testid={NAV.container}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "backdrop-blur-2xl bg-black/70 border-b border-[var(--border-metal)]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <NavLogo />
        <NavLinks />
        <NavCta />
      </div>
    </nav>
  );
};

/* ---------- Hero ---------- */
const HeroBackdrop = () => (
  <>
    <div className="absolute inset-0 metal-texture" />
    <div className="absolute top-1/4 -left-32 w-[480px] h-[480px] rounded-full bg-[radial-gradient(circle,rgba(212,165,76,0.25)_0%,transparent_70%)] blur-3xl glow-orb" />
    <div
      className="absolute bottom-1/4 -right-32 w-[520px] h-[520px] rounded-full bg-[radial-gradient(circle,rgba(138,106,31,0.18)_0%,transparent_70%)] blur-3xl glow-orb"
      style={{ animationDelay: "2s" }}
    />
    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[var(--bg-void)]" />
    <div className="absolute top-24 left-6 lg:left-12 w-12 h-12 border-l border-t border-[var(--gold)]/40" />
    <div className="absolute top-24 right-6 lg:right-12 w-12 h-12 border-r border-t border-[var(--gold)]/40" />
  </>
);

const HeroCtas = () => (
  <div className="flex flex-wrap gap-4">
    <a
      data-testid={HERO.ctaPrimary}
      href="#book"
      className="group inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-b from-[var(--gold-bright)] to-[var(--gold)] text-black font-mono-accent text-[12px] tracking-[0.28em] uppercase font-bold hover:from-[#ffe5a3] hover:to-[var(--gold-bright)] transition-all duration-400 gold-glow-hover"
    >
      Book a call
      <ArrowUpRight size={16} strokeWidth={2} className="group-hover:rotate-45 transition-transform duration-400" />
    </a>
    <a
      data-testid={HERO.ctaSecondary}
      href="#services"
      className="inline-flex items-center gap-3 px-8 py-4 border border-[var(--border-metal)] text-[var(--text-primary)] font-mono-accent text-[12px] tracking-[0.28em] uppercase hover:border-[var(--gold)] hover:text-[var(--gold-bright)] transition-all duration-400"
    >
      Explore services
    </a>
  </div>
);

const HeroTrustRow = () => (
  <div className="mt-16 flex items-center gap-6">
    <div className="flex -space-x-2">
      {Array.from({ length: HERO_STAR_COUNT }, (_, i) => (
        <Star key={`hero-star-${i}`} size={16} fill="#d4a54c" stroke="none" />
      ))}
    </div>
    <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-[var(--text-muted)]">
      Trusted by founders &amp; operators worldwide.
    </p>
  </div>
);

const HeroCopy = () => (
  <div className="lg:col-span-7 reveal">
    <Overline className="mb-8">Growth · Creative · AI · Automation</Overline>
    <h1
      data-testid={HERO.title}
      className="font-display font-light leading-[0.92] tracking-tight text-[var(--text-primary)] text-[clamp(2.8rem,7vw,6.2rem)] mb-8"
    >
      Built to <span className="italic gold-gradient-text font-medium">close</span>.<br />
      Engineered to <span className="italic gold-gradient-text font-medium">scale</span>.
    </h1>
    <p
      data-testid={HERO.subtitle}
      className="font-light text-[var(--text-secondary)] text-lg lg:text-xl max-w-xl leading-relaxed mb-10"
    >
      I&apos;m <span className="text-[var(--gold-bright)]">Harver G. Luciano</span> — content, media, sales and marketing engineered into one system. From paid ads to AI automation, every move is calibrated to convert.
    </p>
    <HeroCtas />
    <HeroTrustRow />
  </div>
);

const HeroEmblem = () => (
  <div className="lg:col-span-5 flex justify-center reveal" style={{ animationDelay: "0.2s" }}>
    <div className="relative">
      <div className="absolute inset-0 bg-[radial-gradient(circle,rgba(240,200,112,0.25)_0%,transparent_60%)] blur-2xl scale-125" />
      <img
        data-testid={HERO.logo}
        src={ASSETS.logo}
        alt="Harver G. Luciano — The Closer.irl"
        className="relative w-full max-w-md float-soft drop-shadow-[0_20px_60px_rgba(212,165,76,0.4)]"
      />
    </div>
  </div>
);

const Hero = () => (
  <section
    id="top"
    data-testid={HERO.section}
    className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden grain"
  >
    <HeroBackdrop />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-12 w-full grid lg:grid-cols-12 gap-12 items-center">
      <HeroCopy />
      <HeroEmblem />
    </div>
    <HeroMarquee />
  </section>
);

/* ---------- About / Ethos ---------- */
const About = () => (
  <section id="about" data-testid={ABOUT.section} className="relative py-32 lg:py-40 overflow-hidden">
    <div className="absolute inset-0 bg-gradient-to-b from-[var(--bg-void)] via-[var(--bg-surface)] to-[var(--bg-void)]" />
    <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
      <div className="grid lg:grid-cols-12 gap-16 items-start">
        <div className="lg:col-span-4">
          <Overline className="mb-6">The Ethos</Overline>
          <h2 data-testid={ABOUT.heading} className="font-display font-light text-5xl lg:text-6xl leading-[1.05] text-[var(--text-primary)]">
            Strategy in <span className="italic gold-gradient-text">silence</span>.<br />
            Execution in <span className="italic gold-gradient-text">scale</span>.
          </h2>
        </div>

        <div className="lg:col-span-8 lg:pl-12 space-y-8 text-[var(--text-secondary)] text-lg leading-relaxed font-light">
          <p>
            Most agencies sell tactics. <span className="text-[var(--gold-bright)]">The Closer.irl</span> sells outcomes — a vertically integrated growth stack built on paid advertising, conversion-grade creative, AI automation, and a human touch where it actually matters.
          </p>
          <p className="text-[var(--text-muted)]">
            Ten disciplines. One operator. Every dollar accountable. Every funnel measured. Every campaign architected to close — not just impress.
          </p>

          <div className="grid sm:grid-cols-3 gap-8 pt-8">
            {[
              { k: "Disciplines", v: "10" },
              { k: "Avg. ROAS", v: "4.7×" },
              { k: "Markets", v: "Global" },
            ].map((item) => (
              <div key={item.k} className="border-t border-[var(--border-metal)] pt-4">
                <div className="font-display text-4xl text-[var(--gold-bright)] mb-1">{item.v}</div>
                <div className="font-mono-accent text-[10px] tracking-[0.28em] uppercase text-[var(--text-muted)]">{item.k}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

/* ---------- Trusted By / Recognized Brands ---------- */
const ClientLogoCard = ({ client, index }) => {
  const isLight = client.bg === "light";
  const plateClass = isLight ? "bg-white rounded-sm" : "bg-transparent";
  const platePadding = isLight ? "12px 20px" : "0";
  return (
    <div
      data-testid={TRUSTED.logo(index)}
      className="metal-card group flex flex-col items-center justify-center p-10 lg:p-14 min-h-[220px]"
    >
      <div
        className={`w-full h-32 flex items-center justify-center overflow-hidden ${plateClass}`}
        style={{ padding: platePadding }}
      >
        <img
          src={client.src}
          alt={client.name}
          className="max-h-full max-w-full object-contain transition-all duration-500 group-hover:scale-105"
          loading="lazy"
        />
      </div>
      <div className="mt-6 font-mono-accent text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)] group-hover:text-[var(--gold)] transition-colors duration-400 text-center">
        {client.name}
      </div>
    </div>
  );
};

const TrustedBy = () => (
  <section
    id="trusted"
    data-testid={TRUSTED.section}
    className="relative py-24 lg:py-32 border-y border-[var(--border-metal)] overflow-hidden"
  >
    <div className="absolute inset-0 bg-gradient-to-b from-[var(--bg-void)] via-black to-[var(--bg-void)]" />
    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[300px] bg-[radial-gradient(ellipse,rgba(212,165,76,0.09)_0%,transparent_70%)] blur-2xl" />

    <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
      <div className="text-center mb-14">
        <Overline className="justify-center inline-flex mb-5">Recognized By</Overline>
        <h2
          data-testid={TRUSTED.heading}
          className="font-display font-light text-4xl lg:text-5xl text-[var(--text-primary)]"
        >
          Brands that <span className="italic gold-gradient-text">closed</span> with us.
        </h2>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border-metal)]">
        {CLIENT_LOGOS.map((client, i) => (
          <ClientLogoCard key={client.name} client={client} index={i} />
        ))}
      </div>

      <p className="mt-12 text-center font-mono-accent text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)]">
        Aviation · Luxury · Lifestyle — trusted with real growth mandates
      </p>
    </div>
  </section>
);

/* ---------- Services Bento ---------- */
const ServicesHeader = () => (
  <div className="grid lg:grid-cols-12 gap-8 mb-20">
    <div className="lg:col-span-6">
      <Overline className="mb-6">Services we provide</Overline>
      <h2 className="font-display font-light text-5xl lg:text-7xl leading-[0.98] text-[var(--text-primary)]">
        Growth. Creative. AI.<br />
        <span className="italic gold-gradient-text">Built to scale.</span>
      </h2>
    </div>
    <div className="lg:col-span-5 lg:col-start-8 flex items-end">
      <p className="text-[var(--text-secondary)] text-lg font-light leading-relaxed">
        A complete operating system for modern brands — from the first click of a paid ad, to the AI agent that closes the deal at 3 a.m.
      </p>
    </div>
  </div>
);

const ServiceCard = ({ service, index }) => {
  const Icon = ICON_MAP[service.icon];
  return (
    <article
      data-testid={SERVICES.card(index)}
      className="metal-card p-7 lg:p-8 min-h-[400px] flex flex-col group"
    >
      <div className="flex items-start justify-between mb-8">
        <span className="font-mono-accent text-[11px] tracking-[0.3em] text-[var(--gold)]">
          {service.num}
        </span>
        <Icon
          size={28}
          strokeWidth={1.2}
          className="text-[var(--gold)] group-hover:text-[var(--gold-bright)] group-hover:scale-110 transition-all duration-500"
        />
      </div>

      <h3 className="font-display text-2xl lg:text-3xl font-light leading-[1.1] text-[var(--text-primary)] mb-1 hyphens-auto" lang="en">
        {service.title}
      </h3>
      <p className="font-mono-accent text-[10px] tracking-[0.28em] uppercase text-[var(--gold)] mb-5">
        {service.subtitle}
      </p>
      <p className="text-[var(--text-secondary)] text-sm leading-relaxed mb-6 font-light">
        {service.desc}
      </p>

      <ul className="mt-auto space-y-2 pt-4 border-t border-[var(--border-metal)]">
        {service.bullets.map((b) => (
          <li key={b} className="flex items-start gap-2 text-[13px] text-[var(--text-muted)] font-light">
            <ChevronRight size={12} className="text-[var(--gold)] mt-1 flex-shrink-0" strokeWidth={2} />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </article>
  );
};

const Services = () => (
  <section id="services" data-testid={SERVICES.section} className="relative py-32 lg:py-40">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <ServicesHeader />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-px bg-[var(--border-metal)]">
        {SERVICES_DATA.map((service, idx) => (
          <ServiceCard key={service.num} service={service} index={idx} />
        ))}
      </div>
    </div>
  </section>
);

/* ---------- Results / Stats ---------- */
const Results = () => {
  const stats = [
    { key: "campaigns", v: "120+", l: "Campaigns Shipped" },
    { key: "roas", v: "4.7×", l: "Average ROAS" },
    { key: "leads", v: "38K+", l: "Qualified Leads" },
    { key: "uptime", v: "24/7", l: "AI Coverage" },
  ];

  return (
    <section id="results" data-testid={RESULTS.section} className="relative py-32 lg:py-40 border-y border-[var(--border-metal)] overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-br from-[var(--bg-surface)] via-[var(--bg-void)] to-[var(--bg-surface)]" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] rounded-full bg-[radial-gradient(circle,rgba(212,165,76,0.08)_0%,transparent_60%)] blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="text-center mb-20">
          <Overline className="justify-center mb-6 inline-flex">The Proof</Overline>
          <h2 className="font-display font-light text-5xl lg:text-6xl text-[var(--text-primary)]">
            Numbers don&apos;t <span className="italic gold-gradient-text">whisper</span>.
          </h2>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-[var(--border-metal)]">
          {stats.map((s) => (
            <div
              key={s.key}
              data-testid={RESULTS.stat(s.key)}
              className="bg-[var(--bg-void)] p-10 lg:p-14 text-center hover:bg-[var(--bg-surface)] transition-colors duration-500"
            >
              <div className="font-display font-light text-6xl lg:text-7xl mb-3 gold-gradient-text">
                {s.v}
              </div>
              <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-[var(--text-muted)]">
                {s.l}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-20 max-w-3xl mx-auto text-center">
          <p className="font-display italic text-2xl lg:text-3xl text-[var(--text-secondary)] leading-relaxed">
            &ldquo;He didn&apos;t just run our ads — he rebuilt our entire growth engine. Three months in, our pipeline tripled.&rdquo;
          </p>
          <div className="mt-6 font-mono-accent text-[10px] tracking-[0.3em] uppercase text-[var(--gold)]">
            — Founder, DTC Brand · $2.4M ARR
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Booking Form ---------- */
const BUDGET_OPTIONS = [
  { value: "<$2K", label: "Under $2,000" },
  { value: "$2K-$5K", label: "$2,000 – $5,000" },
  { value: "$5K-$10K", label: "$5,000 – $10,000" },
  { value: "$10K-$25K", label: "$10,000 – $25,000" },
  { value: "$25K+", label: "$25,000+" },
];

const INITIAL_FORM = {
  name: "",
  email: "",
  company: "",
  service: "",
  budget: "",
  message: "",
};

const BookingContactInfo = () => (
  <div className="lg:col-span-5">
    <Overline className="mb-6">Book a call</Overline>
    <h2 className="font-display font-light text-5xl lg:text-6xl leading-[1.02] text-[var(--text-primary)] mb-8">
      Let&apos;s <span className="italic gold-gradient-text">close</span> something together.
    </h2>
    <p className="text-[var(--text-secondary)] text-lg font-light leading-relaxed mb-10">
      Tell me about your business, your goal, and your timeline. I&apos;ll come back with a tailored growth plan within 24 hours.
    </p>

    <div className="space-y-5">
      <a href="mailto:TheCloser.irl@icloud.com" className="flex items-center gap-4 group">
        <Mail size={18} className="text-[var(--gold)]" strokeWidth={1.5} />
        <span className="font-mono-accent text-[12px] tracking-[0.2em] uppercase text-[var(--text-secondary)] group-hover:text-[var(--gold-bright)] transition" style={{ textTransform: "none" }}>
          TheCloser.irl@icloud.com
        </span>
      </a>
      <a href="https://instagram.com/thecloser.irl" target="_blank" rel="noopener noreferrer" className="flex items-center gap-4 group">
        <Instagram size={18} className="text-[var(--gold)]" strokeWidth={1.5} />
        <span className="font-mono-accent text-[12px] tracking-[0.2em] uppercase text-[var(--text-secondary)] group-hover:text-[var(--gold-bright)] transition" style={{ textTransform: "none" }}>
          @thecloser.irl
        </span>
      </a>
    </div>

    <div className="mt-12 p-6 border border-[var(--border-metal)] bg-black/40">
      <div className="font-mono-accent text-[10px] tracking-[0.3em] uppercase text-[var(--gold)] mb-2">
        Response time
      </div>
      <div className="font-display text-3xl text-[var(--text-primary)]">Under 24h</div>
    </div>
  </div>
);

const BookingSuccess = ({ onReset }) => (
  <div data-testid={BOOKING.success} className="metal-card p-12 lg:p-16 text-center">
    <div className="w-16 h-16 mx-auto mb-6 rounded-full border border-[var(--gold)] flex items-center justify-center">
      <Sparkles size={28} className="text-[var(--gold-bright)]" strokeWidth={1.5} />
    </div>
    <h3 className="font-display text-4xl lg:text-5xl font-light text-[var(--text-primary)] mb-4">
      Request <span className="italic gold-gradient-text">received</span>.
    </h3>
    <p className="text-[var(--text-secondary)] text-lg font-light mb-8">
      Harver will personally reach out within 24 hours.
    </p>
    <button
      type="button"
      onClick={onReset}
      className="font-mono-accent text-[11px] tracking-[0.28em] uppercase text-[var(--gold)] hover:text-[var(--gold-bright)] transition"
    >
      Submit another request →
    </button>
  </div>
);

const BookingForm = ({ form, submitting, onChange, onSubmit }) => (
  <form data-testid={BOOKING.form} onSubmit={onSubmit} className="metal-card p-8 lg:p-12 space-y-7">
    <div className="grid sm:grid-cols-2 gap-7">
      <div>
        <label className="lux-label">Full name *</label>
        <input
          data-testid={BOOKING.inputName}
          className="lux-input"
          placeholder="Your name"
          value={form.name}
          onChange={onChange("name")}
          required
        />
      </div>
      <div>
        <label className="lux-label">Email *</label>
        <input
          data-testid={BOOKING.inputEmail}
          type="email"
          className="lux-input"
          placeholder="you@brand.com"
          value={form.email}
          onChange={onChange("email")}
          required
        />
      </div>
    </div>

    <div className="grid sm:grid-cols-2 gap-7">
      <div>
        <label className="lux-label">Company</label>
        <input
          data-testid={BOOKING.inputCompany}
          className="lux-input"
          placeholder="Brand or company"
          value={form.company}
          onChange={onChange("company")}
        />
      </div>
      <div>
        <label className="lux-label">Budget range</label>
        <select
          data-testid={BOOKING.selectBudget}
          className="lux-input cursor-pointer"
          value={form.budget}
          onChange={onChange("budget")}
          style={{ color: form.budget ? "var(--text-primary)" : "var(--text-muted)" }}
        >
          <option value="" className="bg-[var(--bg-surface)]">Select budget</option>
          {BUDGET_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value} className="bg-[var(--bg-surface)]">{opt.label}</option>
          ))}
        </select>
      </div>
    </div>

    <div>
      <label className="lux-label">Service of interest *</label>
      <select
        data-testid={BOOKING.selectService}
        className="lux-input cursor-pointer"
        value={form.service}
        onChange={onChange("service")}
        required
        style={{ color: form.service ? "var(--text-primary)" : "var(--text-muted)" }}
      >
        <option value="" className="bg-[var(--bg-surface)]">Select a service</option>
        {SERVICES_DATA.map((s) => {
          const cleanTitle = s.title.replace(/\u00ad/g, "");
          return (
            <option key={s.num} value={cleanTitle} className="bg-[var(--bg-surface)]">
              {cleanTitle} — {s.subtitle}
            </option>
          );
        })}
        <option value="Multiple / Custom" className="bg-[var(--bg-surface)]">Multiple / Custom package</option>
      </select>
    </div>

    <div>
      <label className="lux-label">Tell me about the project *</label>
      <textarea
        data-testid={BOOKING.inputMessage}
        className="lux-input resize-none"
        rows={5}
        placeholder="Goal, timeline, current setup..."
        value={form.message}
        onChange={onChange("message")}
        required
      />
    </div>

    <button
      data-testid={BOOKING.submit}
      type="submit"
      disabled={submitting}
      className="w-full mt-4 inline-flex items-center justify-center gap-3 px-8 py-5 bg-gradient-to-b from-[var(--gold-bright)] to-[var(--gold)] text-black font-mono-accent text-[12px] tracking-[0.3em] uppercase font-bold hover:from-[#ffe5a3] hover:to-[var(--gold-bright)] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-400 gold-glow-hover"
    >
      {submitting ? "Sending..." : "Send request"}
      {!submitting && <ArrowUpRight size={16} strokeWidth={2} />}
    </button>

    <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-[var(--text-muted)] text-center pt-2">
      Confidential · No spam · 24h reply guarantee
    </p>
  </form>
);

const Booking = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const isValid = () => Boolean(form.name && form.email && form.service && form.message);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isValid()) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, form);
      setSubmitted(true);
      toast.success("Request received. Harver will reach out within 24h.");
      setForm(INITIAL_FORM);
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="book" data-testid={BOOKING.section} className="relative py-32 lg:py-40 overflow-hidden">
      <div className="absolute inset-0 metal-texture" />
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-[radial-gradient(circle,rgba(212,165,76,0.15)_0%,transparent_70%)] blur-3xl" />

      <div className="relative max-w-6xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-16">
          <BookingContactInfo />
          <div className="lg:col-span-7">
            {submitted ? (
              <BookingSuccess onReset={() => setSubmitted(false)} />
            ) : (
              <BookingForm
                form={form}
                submitting={submitting}
                onChange={handleChange}
                onSubmit={handleSubmit}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Footer ---------- */
const Footer = () => (
  <footer data-testid={FOOTER.container} className="relative border-t border-[var(--border-metal)] bg-black py-16">
    <div className="max-w-7xl mx-auto px-6 lg:px-12">
      <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
        <div className="flex items-center gap-4">
          <img src={ASSETS.logo} alt="logo" className="h-14 w-auto" />
          <div>
            <div className="font-display text-lg text-[var(--text-primary)] tracking-wider">HARVER G. LUCIANO</div>
            <div className="font-mono-accent text-[9px] tracking-[0.3em] text-[var(--gold)] uppercase">The Closer.irl</div>
          </div>
        </div>

        <div className="flex items-center gap-6">
          <a href="https://instagram.com/thecloser.irl" target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="text-[var(--text-muted)] hover:text-[var(--gold-bright)] transition">
            <Instagram size={18} strokeWidth={1.5} />
          </a>
          <a href="mailto:TheCloser.irl@icloud.com" aria-label="Email" className="text-[var(--text-muted)] hover:text-[var(--gold-bright)] transition">
            <Mail size={18} strokeWidth={1.5} />
          </a>
        </div>

        <div className="font-mono-accent text-[10px] tracking-[0.28em] uppercase text-[var(--text-muted)]">
          © {new Date().getFullYear()} The Closer.irl · All rights reserved
        </div>
      </div>
    </div>
  </footer>
);

/* ---------- App ---------- */
function App() {
  if (typeof window !== "undefined" && window.location.pathname.startsWith("/admin")) {
    return <AdminApp />;
  }
  return (
    <div className="App">
      <Toaster position="top-right" theme="dark" toastOptions={TOASTER_OPTIONS} />
      <Navigation />
      <Hero />
      <SectionDivider />
      <About />
      <TrustedBy />
      <Services />
      <Results />
      <Booking />
      <Footer />
    </div>
  );
}

export default App;
